import { useSyncExternalStore } from 'react'

export type ThinkingMode = 'compact' | 'balanced' | 'verbose'
export type TimelinePreferences = {
  gapMinutes: number
  thinkingMode: ThinkingMode
}
export const THINKING_MODES = [
  {
    value: 'compact',
    label: 'Compact',
    description: 'Latest trace on one line. Expand for the full transcript.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Expand the latest trace and collapse earlier traces.',
  },
  {
    value: 'verbose',
    label: 'Verbose',
    description: 'Keep every published trace expanded in the timeline.',
  },
] as const
const KEY = 'canopy:timeline-preferences'
const DEFAULTS: TimelinePreferences = {
  gapMinutes: 15,
  thinkingMode: 'compact',
}
let cached: TimelinePreferences = DEFAULTS
let raw: string | null | undefined
let storageUnavailable = false
const listeners = new Set<() => void>()

export function readTimelinePreferences(): TimelinePreferences {
  if (storageUnavailable) return cached
  try {
    const value = localStorage.getItem(KEY)
    if (raw !== value) {
      raw = value
      const parsed = (value ? JSON.parse(value) : {}) ?? {}
      cached = {
        gapMinutes:
          Number.isFinite(parsed.gapMinutes) &&
          parsed.gapMinutes >= 1 &&
          parsed.gapMinutes <= 1440
            ? parsed.gapMinutes
            : 15,
        thinkingMode: THINKING_MODES.some(
          (mode) => mode.value === parsed.thinkingMode,
        )
          ? parsed.thinkingMode
          : 'compact',
      }
    }
  } catch {
    /* Device preferences remain usable when storage is unavailable. */
  }
  return cached
}

export function saveTimelinePreferences(
  patch: Partial<TimelinePreferences>,
): void {
  const next = { ...readTimelinePreferences(), ...patch }
  if (
    !Number.isFinite(next.gapMinutes) ||
    next.gapMinutes < 1 ||
    next.gapMinutes > 1440
  )
    return
  if (!THINKING_MODES.some((mode) => mode.value === next.thinkingMode)) return
  cached = next
  try {
    raw = JSON.stringify(next)
    localStorage.setItem(KEY, raw)
  } catch {
    storageUnavailable = true
    /* Keep this session's choice. */
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key === KEY || event.key === null) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function useTimelinePreferences(): TimelinePreferences {
  return useSyncExternalStore(
    subscribe,
    readTimelinePreferences,
    () => DEFAULTS,
  )
}
